import ErrorService from "../../error/error.service";
import { HttpMethod } from "./http-client.type";

/**
 * HttpClientService provides HTTP methods for API usage using fetch.
 * Supports GET, POST, PUT, DELETE requests.
 * All methods throw errors for non-OK responses.
 */
export class HttpClientService {
    private _baseUrl: string;
    private _errorService: ErrorService;

    constructor(baseUrl: string, errorService: ErrorService) {
        this._baseUrl = baseUrl;
        this._errorService = errorService;
    }

    /**
     * Returns headers with JSON content type.
     */
    private jsonHeaders(headers?: HeadersInit): HeadersInit {
        return {
            "Content-Type": "application/json",
            ...(headers || {}),
        };
    }

    /**
     * Internal method to handle fetch requests and errors.
     */
    private async request<Response>(url: string, options?: RequestInit): Promise<Response> {
        const fullUrl = `${this._baseUrl}${url}`;

        try {
            const response = await fetch(fullUrl, options);

            if (!response.ok) {
                throw new Error(`HTTP: ${response.status} - ${response.statusText}`);
            }

            return response.json();
        } catch (error) {
            this._errorService.capture(error);
            throw error;
        }
    }

    /**
     * Performs a GET request.
     */
    public async get<Response>(url: string, options?: RequestInit): Promise<Response> {
        return this.request<Response>(url, {
            ...options,
            method: HttpMethod.GET,
            headers: this.jsonHeaders(options?.headers),
        });
    }

    /**
     * Performs a POST request.
     */
    public async post<Response, Body = unknown>(
        url: string,
        body?: Body,

        options?: RequestInit,
    ): Promise<Response> {
        return this.request<Response>(url, {
            ...options,
            method: HttpMethod.POST,
            headers: this.jsonHeaders(options?.headers),
            body: body ? JSON.stringify(body) : undefined,
        });
    }

    /**
     * Performs a PUT request.
     */
    public async put<Response, Body = unknown>(
        url: string,

        body?: Body,
        options?: RequestInit,
    ): Promise<Response> {
        return this.request<Response>(url, {
            ...options,
            method: HttpMethod.PUT,
            headers: this.jsonHeaders(options?.headers),
            body: body ? JSON.stringify(body) : undefined,
        });
    }

    /**
     * Performs a DELETE request.
     */
    public async delete<Response>(url: string, options?: RequestInit): Promise<Response> {
        return this.request<Response>(url, {
            ...options,
            method: HttpMethod.DELETE,
            headers: this.jsonHeaders(options?.headers),
        });
    }
}

export default HttpClientService;
